import type { IntervalConfig, ReportMetadata, ServiceTransaction, SubstitutionConfig } from '../types';
import { substituteDueDates, type CalendarProject } from './calendarService';
import { addDays, formatDateLocal, heuteBerlin, listDueDatesInRange, parseDateLocal, resolveIntervalConfig } from './dateService';
import { substitutionInspectionMatcher } from './inspectionFigures';
import { getServiceLog } from './serviceJournal';
import { buchungstag, isFlatProofEntry, isPostingEntry } from './serviceLogUtils';

/**
 * Soll/Ist einer Urlaubsvertretung fuer die Vertretungsuebersicht der
 * Verwaltung und das Vertretungs-Banner im Cockpit. Rein, ohne React —
 * pruefbar ohne DOM.
 *
 * ─── Ist: jede Begehung im Zeitraum, gleich von wem ───────────────────────
 * Bisher zaehlte nur, was der EINGETEILTE Vertreter gebucht hatte. In der
 * Praxis uebernimmt oft ein Kollege, ohne dass jemand die Vertretung in der
 * App umstellt (eingeteilt Ronny, begangen hat Michael) — die Liste blieb dann
 * bei 0, obwohl die Baustelle begangen war. Gezaehlt werden jetzt die TAGE mit
 * Begehung im Zeitraum, auch die des Koordinators selbst (Betreiber,
 * 23.10.2026); wer begangen hat, steht daneben (`visitors`), und wer nicht
 * der Eingeteilte ist, eigens (`otherVisitors`).
 *
 * Eine Tochter bucht ihre Begehung ins Protokoll der MUTTER (Kind-Form
 * `RPT-CHILD-<Tochter>-<Nr>`, siehe serviceLogUtils.childBookingRef). Ohne
 * `parent` sah die Uebersicht fuer Toechter nie eine Begehung. Umgekehrt
 * zaehlen Kind-Zeilen im Protokoll der Mutter NICHT fuer die Mutter — sie
 * sind Begehungen der Tochter.
 *
 * ─── Soll: EINE Terminreihe ueber den Zeitraum ────────────────────────────
 * Vorher Dauer in Tagen geteilt durch den Turnus, und zwar ohne den Endtag:
 * eine eintaegige Vertretung ergab 0 Tage und damit „0 / 0".
 *
 * Der geplante naechste Termin (`next_scheduled_date`) rueckt mit jeder
 * Begehung weiter. Fuer Vergangenes taugt er deshalb nicht: nach einer
 * spaeteren Begehung laege er hinter dem Zeitraum, und eine unvollstaendige
 * Vertretung saehe erledigt aus (Codex-Befund). Die Reihe entsteht deshalb in
 * zwei Stuecken, die nahtlos aneinanderschliessen:
 *
 *  1. Bis zur LETZTEN Begehung im Zeitraum: Turnus ab der letzten Begehung
 *     davor, an jeder Begehung neu angesetzt — so, wie der Plan damals
 *     mitgewandert ist. Eine Begehung vor dem Folgetermin erfuellt den Termin,
 *     auch verspaetet (in der App wartet der Plan, bis jemand da war); kam bis
 *     zum Folgetermin niemand, ist der Termin verpasst.
 *  2. Danach: der heutige Plan — er ist genau ab dieser Begehung gerechnet,
 *     samt Verschiebungen. Nur wenn er nicht dazu passt (aelter als die
 *     Begehung, hinter dem Zeitraum, oder nach dem Zeitraum wurde schon wieder
 *     begangen), wird ab der Begehung weiter nachgerechnet.
 *
 * Termine vor heute ohne Begehung sind verpasst (`missedDates`), ab heute
 * offen (`openDates`). Soll = begangene Tage + verpasste + offene Termine.
 * Der Turnus laeuft dabei immer ueber `listDueDatesInRange` bzw.
 * `substituteDueDates` — dieselben Termine wie in Planer, Mail und ICS.
 */

export interface SubstitutionVisitor {
    id: string;
    name: string;
}

export interface SubstitutionFigures {
    /** Tage mit Begehung im Zeitraum, gleich von wem. */
    done: number;
    /** Begangene Tage plus verpasste und offene Turnustermine im Zeitraum. */
    expected: number;
    /** 0–100; ohne Soll 0. */
    percentage: number;
    /** Vergangene Turnustermine im Zeitraum ohne Begehung (YYYY-MM-DD), aufsteigend. */
    missedDates: string[];
    /** Turnustermine ab heute im Zeitraum (YYYY-MM-DD), aufsteigend. */
    openDates: string[];
    /** Wer im Zeitraum begangen hat, je Person einmal, nach erstem Besuch. */
    visitors: SubstitutionVisitor[];
    /** Begehende, die nicht der eingeteilte Vertreter sind. */
    otherVisitors: SubstitutionVisitor[];
    /** Kein berechenbarer Turnus (manuell, SiFa) — dann gibt es kein Soll. */
    noSchedule: boolean;
}

export type SubstitutionProject = CalendarProject & { name?: string | null };

export interface SubstitutionProgressInput {
    project: SubstitutionProject;
    entry: SubstitutionConfig;
    /** Die Mutter, wenn `project` eine Tochter ist — dort stehen ihre Buchungen. */
    parent?: { data?: { metadata?: unknown } | null } | null;
    /** Aufgeloeste id des Eingeteilten (gespeichert oder ueber die E-Mail). */
    substituteId?: string | null;
    /** Name zu einer Nutzer-id, fuer Zeilen ohne `performerName`. */
    nameOf?: (id: string) => string;
    /** Bezugstag YYYY-MM-DD in Berlin; ohne Angabe heute. */
    today?: string;
}

const CHILD_REF = 'RPT-CHILD-';
const CHILD_TEXT = 'Tochter-Projekt "';
/** `substituteDueDates` kappt sonst bei 60 — beim taeglichen Turnus zu wenig. */
const MAX_TERMINE = 400;

const metaOf = (p: { data?: { metadata?: unknown } | null } | null | undefined): ReportMetadata | undefined =>
    (p?.data?.metadata as ReportMetadata | undefined) || undefined;

const asKey = (value: string | null | undefined): string | null => {
    const d = parseDateLocal(value || '');
    return d ? formatDateLocal(d) : null;
};

const shiftKey = (key: string, days: number): string => {
    const d = parseDateLocal(key);
    return d ? formatDateLocal(addDays(d, days)) : '';
};

const normName = (value: string | null | undefined): string => (value || '').trim().toLowerCase();

/**
 * Kind-Buchung? Der Ref entscheidet; nur Alteintraege OHNE Ref kennen allein
 * den Text — mit `childId` genau dieser Tochter, ohne irgendeiner.
 */
const isChildEntry = (e: ServiceTransaction, childId?: string, childName?: string): boolean => {
    const ref = typeof e.reportRef === 'string' ? e.reportRef : '';
    if (ref) return ref.startsWith(childId ? `${CHILD_REF}${childId}-` : CHILD_REF);
    const text = String(e.description || '');
    if (childId === undefined) return text.startsWith(CHILD_TEXT);
    return !!childName && text.startsWith(`${CHILD_TEXT}${childName}":`);
};

/**
 * Turnustermine in [vonTag, bisTag], gerechnet ab `ankerTag` als letzter
 * Begehung. Der geplante naechste Termin wird ausgeblendet: er ist der von
 * HEUTE, nicht der nach dieser Begehung.
 */
const termineAb = (config: IntervalConfig, ankerTag: string, vonTag: string, bisTag: string, max = MAX_TERMINE): string[] =>
    listDueDatesInRange({ ...config, nextScheduledDate: undefined, lastInspectionDate: ankerTag }, null, vonTag, bisTag, max);

/** Der Turnustermin nach `tag`, ohne obere Grenze durch den Zeitraum. */
const folgeTermin = (config: IntervalConfig, tag: string): string | null =>
    termineAb(config, tag, shiftKey(tag, 1), shiftKey(tag, MAX_TERMINE), 1)[0] || null;

/**
 * Stueck 1: verpasste Termine zwischen `vorAnker` (letzte Begehung vor dem
 * Zeitraum, darf fehlen) und der letzten Begehung in `besuche`. Ohne
 * `vorAnker` beginnt die Reihe an der ersten Begehung — davor ist nichts
 * bekannt.
 */
const verpassteBisLetzterBegehung = (
    config: IntervalConfig,
    vorAnker: string | null,
    besuche: ReadonlyArray<string>,
    startKey: string,
): string[] => {
    const out: string[] = [];
    if (besuche.length === 0) return out;
    let i = 0;
    let termin: string | null;
    if (vorAnker) {
        termin = termineAb(config, vorAnker, startKey, besuche[besuche.length - 1], 1)[0] || null;
    } else {
        termin = folgeTermin(config, besuche[0]);
        i = 1;
    }
    for (let n = 0; termin && i < besuche.length && n < MAX_TERMINE; n++) {
        const folge = folgeTermin(config, termin);
        if (!folge || besuche[i] < folge) {
            termin = folgeTermin(config, besuche[i]);
            i++;
        } else {
            out.push(termin);
            termin = folge;
        }
    }
    return out;
};

export const substitutionFigures = ({
    project,
    entry,
    parent,
    substituteId,
    nameOf,
    today = heuteBerlin(),
}: SubstitutionProgressInput): SubstitutionFigures => {
    const meta = metaOf(project);
    const startKey = asKey(entry.startDate);
    const endKey = asKey(entry.endDate);
    const config = resolveIntervalConfig(meta);
    const noSchedule = config.type === 'manual';
    const empty: SubstitutionFigures = {
        done: 0, expected: 0, percentage: 0, missedDates: [], openDates: [], visitors: [], otherVisitors: [], noSchedule,
    };
    if (!meta || !startKey || !endKey || startKey > endKey) return empty;

    // Kandidaten: eigene Zeilen ohne Kind-Buchungen, dazu die eigenen
    // Kind-Buchungen aus dem Protokoll der Mutter — je mit dem Matcher des
    // Projekts, dessen LV die Zeile traegt.
    const ownMatcher = substitutionInspectionMatcher(meta.globalData?.services);
    const candidates: ServiceTransaction[] = getServiceLog({ metadata: meta })
        .filter(e => !isChildEntry(e) && ownMatcher(e));
    const parentMeta = metaOf(parent);
    if (parentMeta) {
        const parentMatcher = substitutionInspectionMatcher(parentMeta.globalData?.services);
        const childName = (meta.projectName || project.name || '').trim();
        getServiceLog({ metadata: parentMeta }).forEach(e => {
            if (isChildEntry(e, project.id, childName) && parentMatcher(e)) candidates.push(e);
        });
    }

    // Je Tag — auch vor und nach dem Zeitraum, beide braucht die Terminreihe:
    // Nachweiszeilen (Menge 0) belegen die Begehung allein, sonst muss die
    // Tagessumme positiv sein — eine am selben Tag zurueckgenommene Buchung
    // zaehlt nicht.
    const days = new Map<string, { net: number; proof: boolean; people: SubstitutionVisitor[] }>();
    for (const e of candidates) {
        if (!e?.date || isPostingEntry(e)) continue;
        const day = buchungstag(e.date);
        if (!day) continue;
        const slot = days.get(day) || { net: 0, proof: false, people: [] };
        if (isFlatProofEntry(e)) slot.proof = true;
        else slot.net += Number(e.quantity) || 0;
        const id = e.performerId && e.performerId !== 'system' ? e.performerId : '';
        const name = (e.performerName || '').trim() || (id && nameOf ? nameOf(id) : '');
        if ((id || name) && !slot.people.some(p => (id && p.id === id) || (!id && p.name === name))) {
            slot.people.push({ id, name });
        }
        days.set(day, slot);
    }
    const visited = [...days.entries()]
        .filter(([, s]) => s.proof || s.net > 0)
        .sort(([a], [b]) => a.localeCompare(b));
    const inRange = visited.filter(([day]) => day >= startKey && day <= endKey);

    const visitors: SubstitutionVisitor[] = [];
    for (const [, slot] of inRange) {
        for (const p of slot.people) {
            if (!visitors.some(v => (p.id && v.id === p.id) || (!p.id && normName(v.name) === normName(p.name)))) {
                visitors.push(p);
            }
        }
    }
    const plannedName = normName(entry.substituteName);
    const otherVisitors = visitors.filter(v =>
        !(substituteId && v.id === substituteId) && !(plannedName && normName(v.name) === plannedName));

    const besuche = inRange.map(([day]) => day);
    const done = besuche.length;
    if (noSchedule) {
        return { ...empty, done, expected: done, percentage: done > 0 ? 100 : 0, visitors, otherVisitors };
    }

    // Stueck 1: bis zur letzten Begehung im Zeitraum.
    const vorAnker = [visited.filter(([day]) => day < startKey).pop()?.[0], asKey(config.lastInspectionDate)]
        .filter((d): d is string => !!d && d < startKey)
        .sort()
        .pop() || null;
    const verpasst = verpassteBisLetzterBegehung(config, vorAnker, besuche, startKey);

    // Stueck 2: ab dem Tag nach der letzten Begehung (bzw. ab Start).
    const letzterAnker = besuche[besuche.length - 1] || vorAnker;
    const nachAnker = letzterAnker ? shiftKey(letzterAnker, 1) : startKey;
    const restAb = nachAnker > startKey ? nachAnker : startKey;
    const geplant = asKey(project.next_scheduled_date) || asKey(config.nextScheduledDate);
    const spaeterBegangen = visited.some(([day]) => day > endKey);
    const planGilt = !!geplant && !spaeterBegangen && geplant <= endKey && (!letzterAnker || geplant > letzterAnker);
    let rest: string[] = [];
    if (restAb <= endKey) {
        if (planGilt) rest = substituteDueDates(project, { ...entry, startDate: restAb, endDate: endKey }, MAX_TERMINE, letzterAnker);
        else if (letzterAnker) rest = termineAb(config, letzterAnker, restAb, endKey);
    }

    const missedDates = [...verpasst, ...rest.filter(d => d < today)];
    const openDates = rest.filter(d => d >= today);
    const expected = done + missedDates.length + openDates.length;
    const percentage = expected > 0 ? Math.round((done / expected) * 100) : 0;

    return { done, expected, percentage, missedDates, openDates, visitors, otherVisitors, noSchedule };
};
